import { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { com } from '@eidu/web'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import LoginIdentifierField from '../../../../components/input/LoginIdentifierField'
import useHasGlobalPermissions from '../../../permission/useHasGlobalPermissions'
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import PhoneNumber = com.eidu.sharedlib.user.PhoneNumber
import isRealMobilePhoneNumber = com.eidu.sharedlib.user.isRealMobilePhoneNumber
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import { KindEditPageProps } from '../KindPages'
import computeLabel from '../shared/computeLabel'

const isValidEmailOrEmpty = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return email === '' || emailRegex.test(email)
}

const isValidPhoneNumberOrEmpty = (phone: string): boolean => {
  const phoneNumber = PhoneNumber.fromString(phone)
  return phone === '' || !!(phoneNumber && isRealMobilePhoneNumber(phoneNumber))
}

const UserEditPage = ({ entity, entityId, entityType, entityTypes, loading }: KindEditPageProps) => {
  const {
    fields,
    updateField,
    submitting,
    hasAttemptedSubmit,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerExternalValues,
    registerValidation,
  } = useEntityEdit({
    entity,
    entityId,
    entityType,
  })

  const externalValues = entity?.entity.externalValues?.asJsReadonlyMapView() ?? new Map()
  const [phone, setPhone] = useState<string>(() => externalValues.get(phoneNumberLoginIdentifierKey) || '')
  const [email, setEmail] = useState<string>(() => externalValues.get(emailLoginIdentifierKey) || '')
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManageAuthentication)
  const showAuthenticationInfo = hasPermission(GlobalAction.ManageAuthentication)

  useEffect(() => {
    if (!isValidEmailOrEmpty(email) && hasAttemptedSubmit) {
      setEmailError('Invalid email format')
    } else {
      setEmailError(undefined)
    }

    if (!isValidPhoneNumberOrEmpty(phone) && hasAttemptedSubmit) {
      setPhoneError('Invalid phone number format')
    } else {
      setPhoneError(undefined)
    }
  }, [email, phone, hasAttemptedSubmit])

  const validate = useCallback((): boolean => {
    let hasEmailError = false
    let hasPhoneError = false

    if (!isValidEmailOrEmpty(email)) {
      setEmailError('Invalid email format')
      hasEmailError = true
    }

    if (!isValidPhoneNumberOrEmpty(phone)) {
      setPhoneError('Invalid phone number format')
      hasPhoneError = true
    }

    return !(hasEmailError || hasPhoneError)
  }, [email, phone])

  useEffect(() => {
    registerValidation(validate)
  }, [registerValidation, validate])

  useEffect(() => {
    const externalValues = new Map()
    externalValues.set(phoneNumberLoginIdentifierKey, PhoneNumber.fromString(phone)?.normalized || null)
    externalValues.set(emailLoginIdentifierKey, email || null)
    registerExternalValues(externalValues)
  }, [registerExternalValues, phone, email])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const label = computeLabel(entityType, fields)

  return (
    <PageLayout
      title={label ?? entityType.name}
      subtitle={label ? entityType.name : undefined}
      showCancel
      showSave
      showDelete={!!entity}
      saveDisabled={!valid}
      submitting={submitting}
      onSave={submitEntity}
      onDelete={deleteEntity}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 1.5 }}>
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={loading}
        />
        {showAuthenticationInfo && (
          <LoginIdentifierField
            phone={phone}
            setPhone={setPhone}
            email={email}
            setEmail={setEmail}
            phoneError={phoneError}
            emailError={emailError}
            disabled={loading}
          />
        )}
      </Box>
    </PageLayout>
  )
}

export default UserEditPage
